import React, { useMemo } from 'react';
import Box from 'ui/Box';
import { useSelector } from 'react-redux';
import { getMarketProducts, getSelectedProduct } from 'ducks/products';
import styled from 'styled-components';
import { ProductThumb } from 'selectedMenu/components';
import { useCore } from 'contexts/core';
import KompisToolTip from 'ui/KompisToolTip/KompisToolTip';
import regular from './images/loftbed_regular.jpg';
import regular_mirrored from './images/loftbed_regular_mirrored.jpg';
import outward_desk from './images/loftbed_outward_desk.jpg';
import outward_desk_mirrored from './images/loftbed_outward_desk_mirrored.jpg';
import { useIntl } from 'react-intl';
import { ProductType } from '@inter-ikea-storageone/range';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import { isMobile } from 'utils/platform';
import { useBehaviorTracker } from 'contexts/behaviors';

const Thumbnails = styled.ul`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  margin: 0;
  padding-left: 0;
`;

const Image = styled.img`
  width: ${({ theme }) => theme.space.xxxl};
  height: ${({ theme }) => theme.space.xxxxl};
  padding-bottom: ${({ theme }) => theme.space.xs};
  display: flex;
  justify-content: center;
`;

const StyledButton = styled.button`
  position: relative;
  background: #fff;
  cursor: pointer;
  width: 100%;
  border: none;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(0, 0, 0, 0);

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 8px 8px rgba(0, 0, 0, 0.04);
    }
  }
  &:focus {
    outline: 0.125rem solid ${({ theme }) => theme.colors.feedback.focus};
  }
`;

const ChangeLoftBedDeskOrientation = ({ ...props }) => {
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();
  const { formatMessage } = useIntl();
  const selectedProduct = useSelector(getSelectedProduct);
  const products = useSelector(getMarketProducts);

  const loftBedProducts = useMemo(() => {
    return products.filter(p => p.type === ProductType.SMASTAD_LOFT_BED);
  }, [products]);

  if (!selectedProduct) {
    return null;
  }

  const canHaveInwardDesk = !selectedProduct?.meta?.loftBedOrientation?.['inward-cabinet'];

  const inwardDeskOption = [
    {
      id: 'regular',
      img: regular,
      toolTip: 'storageone.smastadLoftBedTooltipStairsRightDeskUnderneath',
      'inward-desk': true,
      mirrored: false
    },
    {
      id: 'regular_mirrored',
      img: regular_mirrored,
      toolTip: 'storageone.smastadLoftBedTooltipStairsLeftDeskUnderneath',
      'inward-desk': true,
      mirrored: true
    }
  ];

  const outwardDeskOption = [
    {
      id: 'outward_desk',
      img: outward_desk,
      toolTip: 'storageone.smastadLoftBedTooltipStairsRightDeskOutLeft',
      'inward-desk': false,
      mirrored: false
    },
    {
      id: 'outward_desk_mirrored',
      img: outward_desk_mirrored,
      toolTip: 'storageone.smastadLoftBedTooltipStairsLeftDeskOutRight',
      'inward-desk': false,
      mirrored: true
    }
  ];

  const options = canHaveInwardDesk
    ? [...inwardDeskOption, ...outwardDeskOption]
    : outwardDeskOption;

  const onSelect = (option: string) =>
    behaviorTracker.custom(() => {
      const selectedOption = options.find(o => o.id === option);
      const selectedEntity = coreApi.entities.findSelectedEntity();
      if (!selectedOption || !selectedEntity) return;
      const newProduct = loftBedProducts.find(
        p =>
          p?.meta?.loftBedOrientation?.mirrored === selectedOption?.mirrored &&
          p?.meta?.loftBedOrientation?.['inward-desk'] === selectedOption?.['inward-desk'] &&
          p?.meta?.loftBedOrientation?.['inward-cabinet'] ===
            selectedProduct?.meta?.loftBedOrientation?.['inward-cabinet']
      );
      const result =
        newProduct && coreApi.products.replace(selectedEntity.id, newProduct.coreProductId);
      result &&
        result.replacedEntityResult.placedEntity &&
        coreApi.entities.select(result.replacedEntityResult.placedEntity);
    });

  return (
    <Box mt="md">
      {!canHaveInwardDesk && !isMobile() && (
        <Box>
          <SkapaInlineMessage
            body={formatMessage({ id: 'storageone.smastadLoftBedOrientationDeskInfo' })}
            variant={InlineMessageVariantEnum.INFORMATIVE}
            ssrIcon={InformationCircle}
            subtle
          />
        </Box>
      )}
      <Thumbnails {...props}>
        {options.map(option => (
          <ProductThumb
            data-testid={option.id}
            key={option.id}
            onClick={() => onSelect(option.id)}
            data-selected={
              selectedProduct?.meta?.loftBedOrientation?.mirrored === option.mirrored &&
              selectedProduct?.meta?.loftBedOrientation?.['inward-desk'] === option?.['inward-desk']
            }
          >
            <StyledButton>
              <KompisToolTip text={formatMessage({ id: option.toolTip })} uniqueId={option.id}>
                <Image src={option.img} alt={formatMessage({ id: option.toolTip })} />
              </KompisToolTip>
            </StyledButton>
          </ProductThumb>
        ))}
      </Thumbnails>
    </Box>
  );
};

export default ChangeLoftBedDeskOrientation;
